import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, HostBinding, ViewChild, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ScrollSelectedInViewDirective } from '@frontend/sports/common/core/feature/scroll-selected-in-view';
import { ThemeElevated } from '@frontend/sports/common/ui/shared';
import { CardThemeSurface, SportsThemedCardDirective } from '@frontend/sports/common/ui/sports-themed-card';
import { DsCard } from '@frontend/ui/card';
import { SpeculativeLinkDirective } from '@frontend/vanilla/features/speculative-link';
import { RxFor } from '@rx-angular/template/for';
import { Subject } from 'rxjs';

import { EventCarouselSkeletonComponent } from './event-carousel-skeleton/event-carousel-skeleton.component';
import { EventCarouselCustomItem, EventCarouselItem } from './event-carousel.model';
import { EventDetailCardComponent } from './event-detail-card/event-detail-card.component';

@Component({
    selector: 'ms-event-carousel',
    imports: [
        RxFor,
        CommonModule,
        ScrollAdapterComponent,
        ScrollSelectedInViewDirective,
        EventDetailCardComponent,
        EventCarouselSkeletonComponent,
        SpeculativeLinkDirective,
        DsCard,
        SportsThemedCardDirective,
    ],
    templateUrl: './event-carousel.component.html',
    styleUrl: './event-carousel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventCarouselComponent {
    @HostBinding('class') class = 'event-carousel-container';
    readonly eventTileClicked = output<{ eventCarouselItem: EventCarouselItem; tileIndex: number }>();
    readonly eventCustomTileClicked = output<void>();

    @ViewChild(ScrollAdapterComponent, { static: true }) private scrollAdapter: ScrollAdapterComponent;

    eventCarouselItems = input.required<EventCarouselItem[]>();
    eventCarouselCustomItem = input<EventCarouselCustomItem>();
    activeEventId = input<string>();
    isLoading = input<boolean>();

    itemsRendered = new Subject<EventCarouselItem[]>();

    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        this.itemsRendered.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.scrollAdapter?.setArrows();
        });
    }

    themeElevated: ThemeElevated = {
        'sportingbet-br': { isElevated: false },
        'betboo-br': { isElevated: false },
    };

    readonly cardThemeSurface: CardThemeSurface = {
        'mgm-dice': 'low',
    };

    tileClick(eventCarouselItem: EventCarouselItem, index: number): void {
        this.eventTileClicked.emit({ eventCarouselItem, tileIndex: index });
    }

    trackByEventId(_: number, eventItem: EventCarouselItem): string | undefined {
        return eventItem.event.id;
    }

    getAdjustedIndex(index: number): number {
        return index + (this.eventCarouselCustomItem()?.carouselItemTemplate ? 1 : 0);
    }
}
